import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SportsAuthContext = createContext(null);

// Dedicated axios instance for the Sports realm. Uses a SEPARATE token key
// (`sports_token`) so it can never collide with the Retail session.
export const sportsApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

// ── Support session (super-admin impersonation) ──────────────────────────────
// A support token arrives via /sports/dashboard#support_token=<jwt> (minted by
// the super-admin "Access customer" button). It lives in sessionStorage — one
// tab, dies with the tab — so it can never clobber a real owner login in
// localStorage, and a super admin's own POS/retail session is untouched.
const SUPPORT_KEY = 'sports_support_token';

const stripSupportHash = () => {
  if (/[#&]support_token=/.test(window.location.hash || '')) {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }
};

(() => {
  const m = /[#&]support_token=([^&]+)/.exec(window.location.hash || '');
  if (m) {
    sessionStorage.setItem(SUPPORT_KEY, decodeURIComponent(m[1]));
    // Strip the token from the URL so it never lands in history/bookmarks.
    // A replaceState fired before the window `load` event can be reverted when
    // the browser commits the navigation entry — strip now AND after load.
    stripSupportHash();
    if (document.readyState === 'complete') {
      setTimeout(stripSupportHash, 0);
    } else {
      window.addEventListener('load', () => setTimeout(stripSupportHash, 0), { once: true });
    }
  }
})();

export function getSupportSession() {
  const token = sessionStorage.getItem(SUPPORT_KEY);
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (payload.exp * 1000 < Date.now()) { sessionStorage.removeItem(SUPPORT_KEY); return null; }
    return { token, expiresAt: new Date(payload.exp * 1000), impBy: payload.imp_by || '' };
  } catch { sessionStorage.removeItem(SUPPORT_KEY); return null; }
}

export function endSupportSession() {
  sessionStorage.removeItem(SUPPORT_KEY);
  window.location.href = '/sports/super-admin/tenants';
}

// Support token (this tab) takes precedence over a stored owner login.
export const getActiveSportsToken = () =>
  getSupportSession()?.token || localStorage.getItem('sports_token');

sportsApi.interceptors.request.use((config) => {
  const token = getActiveSportsToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export function SportsAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [token, setToken] = useState(getActiveSportsToken());
  const [loading, setLoading] = useState(true);
  const supportSession = getSupportSession();

  const fetchMe = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const res = await sportsApi.get('/sports/auth/me');
      setUser(res.data.user);
      setOrganization(res.data.organization);
    } catch {
      // A dead support token must not evict a real owner login stored beneath it.
      if (getSupportSession()) sessionStorage.removeItem('sports_support_token');
      else localStorage.removeItem('sports_token');
      setToken(null);
      setUser(null);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  // Belt-and-braces: re-strip after mount as well (module-load strips can race
  // the browser's navigation-entry commit; see stripSupportHash above).
  useEffect(() => { setTimeout(stripSupportHash, 0); }, []);

  const persist = (data) => {
    localStorage.setItem('sports_token', data.token);
    setToken(data.token);
    setUser(data.user);
    setOrganization(data.organization);
  };

  const login = async (email, password) => {
    const res = await sportsApi.post('/sports/auth/login', { email, password });
    persist(res.data);
    return res.data;
  };

  const register = async (payload) => {
    const res = await sportsApi.post('/sports/auth/register', payload);
    persist(res.data);
    return res.data;
  };

  const logout = () => {
    if (getSupportSession()) { endSupportSession(); return; }
    localStorage.removeItem('sports_token');
    setToken(null);
    setUser(null);
    setOrganization(null);
  };

  return (
    <SportsAuthContext.Provider value={{ user, organization, token, loading, login, register, logout, refresh: fetchMe, supportSession }}>
      {children}
    </SportsAuthContext.Provider>
  );
}

export const useSportsAuth = () => useContext(SportsAuthContext);
